import { useCallback, useEffect } from "react";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import type { TFunction } from "i18next";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useCompleteTask } from "../hooks/useCompleteTask";
import { LANG_BASE, TASK_STATUS, TEST_ID_BASE } from "../VesselStatus";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  vesselId: string;
};

const rolesOptions = [
  { label: "Marine Office", value: "marine-office" },
  { label: "Marine Operator", value: "marine-operator" },
  { label: "Harbour Master", value: "harbour-master" },
];

const getDelegateSchema = (t: TFunction) =>
  z.object({
    delegateTo: z.enum(["marine-office", "marine-operator", "harbour-master"], {
      required_error: t("form-validation.required", { label: t(`${LANG_BASE}.delegate-to`) }),
    }),
    delegationReason: z
      .string()
      .max(
        1000,
        t("form-validation.max-length", { label: t(`${LANG_BASE}.delegation-reason`), max: 1000 }),
      )
      .optional(),
    delegationAttachment: z.string().optional(),
    delegationAttachmentName: z.string().optional(),
  });

const DelegateSideDrawer = ({ isOpen, onClose, vesselId }: Props) => {
  const { t } = useTranslation();
  const { notify } = useNotification();

  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const { clearFilesForKey, setFilesForKey, filesByKey } = useUploadStore();

  const files = filesByKey["delegateAttachments"] || [];

  const uploadConditions = FILE_UPLOAD_PROFILES.sideDrawerApprovalFile;

  const allowedSideDrawerExtensions = uploadConditions.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  const form = useForm({
    mode: "onTouched",
    reValidateMode: "onChange",
    resolver: zodResolver(getDelegateSchema(t)),
    shouldUnregister: true,
  });

  const delegationAttachment = form.watch("delegationAttachment");

  const handleClose = () => {
    form.reset();
    clearFilesForKey("delegateAttachments");
    onClose();
  };

  const handleSuccess = () => {
    handleClose();
  };

  const handleError = () => {
    handleClose();
  };

  const { mutate: completeTask, isPending } = useCompleteTask({ handleSuccess, handleError });

  const onValid = async ({
    delegateTo,
    delegationReason,
    delegationAttachment,
    delegationAttachmentName,
  }: {
    delegateTo: string;
    delegationReason?: string;
    delegationAttachment?: string;
    delegationAttachmentName?: string;
  }) => {
    if (documentUploadMutation.isPending) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }
    completeTask({
      service: "bff-vessel",
      vesselId,
      params: {
        status: TASK_STATUS.DELEGATED,
        internalNotes: delegationReason,
        internalNotesDocument: delegationAttachment,
        internalNotesDocumentName: delegationAttachmentName,
        delegatedToRole: delegateTo,
      },
      successTitle: t("vessel-management.notifications.delegation-success-title"),
      errorTitle: t("vessel-management.notifications.delegation-error-title"),
    });
  };

  const onSubmit = () => {
    form.handleSubmit(onValid)();
  };

  useEffect(() => {
    clearFilesForKey("delegateAttachments");
  }, []);

  const documentUploadMutation = useMutation({
    mutationFn: ({ file, documentType }: { file: File; documentType: string }) =>
      runTransaction(
        {
          module: "vessel_registration",
          screen: "DelegateVesselRegistration",
          action: "upload_vessel_task_document",
          entityType: "vessel_document",
        },
        async (transactionId) => {
          await new Promise((resolve) => setTimeout(resolve, 5000)); // Simulate network latency
          const payloadForSasToken = {
            prefix: SasTokenPrefix.VESSEL_REGISTRATION,
            contentType: file.type || null,
            privilege: SasTokenPrivilege.WRITE,
            blobUrl: null,
          };

          const { blobUrl, sasToken } = await getSasToken(
            payloadForSasToken,
            ...(transactionId ? [transactionId] : []),
          );

          const [uploadResult] = await uploadFileToAzureBlob({
            file,
            blobUrl,
            sasToken,
            certificateType: documentType,
          });

          const documentUrl = uploadResult?.documentUrl ?? blobUrl;
          return { documentUrl, documentType };
        },
      ),
  });

  const handleFileUpload = useCallback(
    (_: string, files: File[]) => {
      const file = files[0];
      if (!file) return;

      documentUploadMutation.mutate(
        { file, documentType: "INTERNAL_NOTES" },
        {
          onSuccess: ({ documentUrl }) => {
            form.setValue("delegationAttachment", documentUrl);
            form.setValue("delegationAttachmentName", file.name);
            setFilesForKey("delegateAttachments", [
              {
                name: file.name,
                url: documentUrl,
              },
            ]);
          },
          onError: () => {
            clearFilesForKey("delegateAttachments");
            notify({
              title: t("vessel-management.notifications.attachments-upload-error"),
              description: t(
                "vessel-management.notifications.attachments-upload-error-description",
              ),
              type: "error",
            });
          },
        },
      );
    },
    [clearFilesForKey, documentUploadMutation, form, notify, setFilesForKey, t],
  );

  const handleRemoveFile = useCallback(() => {
    form.setValue("delegationAttachment", undefined);
    form.setValue("delegationAttachmentName", undefined);
    clearFilesForKey("delegateAttachments");
  }, [clearFilesForKey, form]);

  return (
    <div data-testid={`${TEST_ID_BASE}-delegate-sidedrawer`}>
      <FormProvider {...form}>
        <SideDrawer
          isOpen={isOpen}
          onClose={handleClose}
          onSubmit={onSubmit}
          isSubmitting={isPending}
          keepMounted={false}
          title={t(`${LANG_BASE}.delegate`)}
          description={t(`${LANG_BASE}.delegate-description`)}
          submitLabel={t(`${LANG_BASE}.delegate`)}
        >
          <div className="w-[400px] p-6">
            <FormItem
              name="delegateTo"
              type="select"
              label={t(`${LANG_BASE}.delegate-to`)}
              testId={`${TEST_ID_BASE}-delegate-to`}
              options={rolesOptions}
            />
            <FormItem
              name="delegationReason"
              type="textarea"
              label={t(`${LANG_BASE}.delegation-reason`)}
              testId={`${TEST_ID_BASE}-delegation-reason`}
              minRows={4}
              maxChars={1000}
            />
            <FormItem
              name="delegateAttachments"
              type="file"
              label={t(`${LANG_BASE}.attachments`)}
              testId={`${TEST_ID_BASE}-attachments`}
              allowedFileExtensions={allowedSideDrawerExtensions}
              uploadConditions={uploadConditions}
              maxFiles={1}
              onUpload={handleFileUpload}
              files={files}
            />
            {files[0] && delegationAttachment && (
              <SingleFile
                testId={`${TEST_ID_BASE}-attachment-0`}
                file={files[0]}
                fieldName={files[0].name}
                currentFileIndex={0}
                setFiles={handleRemoveFile}
              />
            )}
          </div>
        </SideDrawer>
      </FormProvider>
    </div>
  );
};

export default DelegateSideDrawer;
